import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import { buildPopup } from './popup';
import { dotColor, drawOrder } from './scans';
import type { Group } from './types';

/** Dark base map: the white scan dots need it. Swap this one constant to change provider. */
export const TILE_URL = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
export const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

export interface MapFocus {
  cellId: string;
  /** Changes on every request so focusing the same cell twice still acts. */
  nonce: number;
}

interface Props {
  groups: Group[];
  onPickPlayer: (playerId: string, label: string) => void;
  focus: MapFocus | null;
}

/** One canvas dot per location. White normally, pink (drawn on top) where a multi-part part was found. */
export function ScanMap({ groups, onPickPlayer, focus }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const markers = useRef(new Map<string, L.CircleMarker>());
  const pick = useRef(onPickPlayer);
  pick.current = onPickPlayer;

  useEffect(() => {
    if (!host.current) return;
    const m = L.map(host.current, { preferCanvas: true, worldCopyJump: true }).setView([20, 0], 2);
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, subdomains: 'abcd', maxZoom: 19 }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    map.current = m;
    return () => {
      m.remove();
      map.current = null;
      layer.current = null;
      markers.current.clear();
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    const l = layer.current;
    if (!m || !l) return;
    l.clearLayers();
    markers.current.clear();
    for (const group of drawOrder(groups)) {
      const marker = L.circleMarker([group.lat, group.lng], {
        radius: 7,
        color: '#1b1f2a',
        weight: 1,
        fillColor: dotColor(group),
        fillOpacity: 1,
      });
      marker.bindPopup(() => buildPopup(group, (id, label) => pick.current(id, label)), { maxWidth: 380, minWidth: 260 });
      marker.addTo(l);
      markers.current.set(group.cellId, marker);
    }
    if (groups.length === 0) {
      m.setView([20, 0], 2);
    } else {
      m.fitBounds(L.latLngBounds(groups.map((g) => [g.lat, g.lng] as [number, number])), { padding: [48, 48], maxZoom: 16 });
    }
  }, [groups]);

  useEffect(() => {
    if (!focus) return;
    const marker = markers.current.get(focus.cellId);
    const m = map.current;
    if (!marker || !m) return;
    m.setView(marker.getLatLng(), Math.max(m.getZoom(), 15));
    marker.openPopup();
  }, [focus]);

  return <div ref={host} className="map" data-testid="map" />;
}
